import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { completeGoogleSignin } from './api';

export const CALLBACK_PATH = '/app/senders/callback';

// Google's code and state are single-use: read them once, drop them from the address bar
// and history, and post them once, even when React mounts the page twice.
let pendingSignin = null;
const completions = new Map();

export function readSigninResult() {
  const params = new URLSearchParams(window.location.search);
  if (params.has('code') || params.has('state') || params.has('error')) {
    pendingSignin = {
      code: params.get('code') || '',
      state: params.get('state') || '',
      error: params.get('error') || '',
    };
    window.history.replaceState(null, '', CALLBACK_PATH);
  }
  return pendingSignin || { code: '', state: '', error: '' };
}

function completeOnce(signin) {
  if (!completions.has(signin.state)) completions.set(signin.state, completeGoogleSignin(signin));
  return completions.get(signin.state);
}

/** Test seam: forget a sign-in read by an earlier render. */
export function resetSigninResult() {
  pendingSignin = null;
  completions.clear();
}

function failureFor(signin) {
  if (signin.error === 'access_denied') return 'You cancelled the Google sign-in, so nothing was connected.';
  if (signin.error) return `Google did not finish the sign-in (${signin.error}). Connect the address again.`;
  if (!signin.code || !signin.state) return 'This page has no sign-in to finish. Connect the address again.';
  return '';
}

/** Where Google returns a member after they sign in to connect an address or allow reading it. */
export default function SenderCallbackPage() {
  const [signin] = useState(readSigninResult);
  const queryClient = useQueryClient();
  const early = failureFor(signin);
  const [result, setResult] = useState(early ? { error: early } : null);

  useEffect(() => {
    if (early) return undefined;
    let active = true;
    completeOnce(signin)
      .then((sender) => {
        queryClient.invalidateQueries({ queryKey: ['person-senders'] });
        if (active) setResult({ sender });
      })
      .catch((failure) => active && setResult({ error: failure.message }));
    return () => {
      active = false;
    };
  }, [early, queryClient, signin]);

  return (
    <main className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
      <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h1 className="text-lg font-semibold text-slate-950">Connect your Gmail address</h1>
        {!result ? (
          <p className="mt-3 text-sm text-slate-600" role="status">Finishing the Google sign-in…</p>
        ) : result.sender ? (
          <p className="mt-3 text-sm text-slate-700" role="status">
            {result.sender.address} is connected. Choose what the Agents and Automations you allow may do with it.
          </p>
        ) : (
          <p className="mt-3 text-sm text-rose-700" role="alert">{result.error}</p>
        )}
        {result ? (
          <Link to="/app/senders" className="mt-5 inline-block text-sm font-semibold text-cyan-800 underline underline-offset-4">
            Back to mailbox access
          </Link>
        ) : null}
      </div>
    </main>
  );
}
